import { useMemo } from "react";
import { AccountLabel } from "@/entities/DataAssociation/Account.model";
import { CATEGORY_METADATA, LabelMetadata } from "../labeling.constants";
import { LabelBase } from "./LabelBase";
import clsx from "clsx";
import { SparklesIcon } from "@heroicons/react/24/outline";
import {
    useHotkey,
    getShortcutDisplay,
} from "@/ui/organisms/FloatingMenu/useHotkey";

export function LabelItem({
    label,
    onClick,
    prettify = true,
    dot = false,
    listItem = false,
    className,
    recommended = false,
    hotkeyNumber,
}: {
    prettify?: boolean;
    dot?: boolean;
    listItem?: boolean;
    label: LabelMetadata | null | undefined;
    onClick?: (labelCode: AccountLabel) => void;
    recommended?: boolean;
    className?: string;
    hotkeyNumber?: number;
}) {
    const command = useMemo(() => {
        if (!hotkeyNumber || !onClick || !label) return undefined;
        return {
            label: label?.name,
            shortcut: {
                key: String(hotkeyNumber),
                mac: `⌘${hotkeyNumber}`,
                windows: `Ctrl+${hotkeyNumber}`,
            },
            onClick: () => onClick(label.code),
        };
    }, [hotkeyNumber, label?.name, label?.code, onClick]);

    useHotkey({ command });

    if (!label) return "Invalid label reference";

    const text = prettify ? label.name : label.code;
    const color = label.category
        ? CATEGORY_METADATA[label.category].color
        : undefined;
    const hotkeyLabel = command ? getShortcutDisplay(command.shortcut) : null;

    if (listItem) {
        return (
            <li
                className={clsx(
                    "card flex items-start justify-between gap-2",
                    recommended && "border-default mb-2 border",
                    className,
                )}
                key={label.name}
                onClick={() => {
                    onClick?.(label.code);
                }}
            >
                <LabelBase text={text} color={color} dot={dot} />
                <div className="flex items-center gap-2">
                    {recommended && (
                        <SparklesIcon className="text-subdued size-4" />
                    )}
                    {hotkeyLabel && (
                        <span
                            className={clsx(
                                "bg-gray-500 px-1 py-0.5",
                                "text-bg-default text-slate-800",
                                "body-micro rounded-md",
                            )}
                        >
                            {hotkeyLabel}
                        </span>
                    )}
                </div>
            </li>
        );
    }

    return (
        <div onClick={() => onClick?.(label.code)} className={className}>
            <LabelBase text={text} color={color} dot={dot} />
        </div>
    );
}
